import { Flex } from "antd"
import { useEffect, useMemo, useRef, useState } from "react"
import { chunk } from "lodash"

import Settings from "./Settings"
import ProjectGroups from "./ProjectGroups"
import { dataRepresentationEnum, campaignStates } from "../../../types/customers"
import { IPlacementDataForUpdate } from "../store/types/placements"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { useActions } from "../../../hooks/useActions"
import usePlacementMetrics from "../hooks/usePlacementMetrics"
import { ORDER_TYPES } from "../constants"
import { weekFilterEnum } from "../types/types"
import KpiSummaryCards from "../../../components/KpiSummaryCards"
import { buildFetchSponsoredProductParams } from "../services/sponsoredProductsParams.service"

const SponsoredProductsContainer: React.FC = () => {
  const [isExpandedAll, setIsExpandedAll] = useState(false)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([])
  const [selectCustomFilters, setSelectCustomFilters] = useState<string[]>([])
  const [placementDataForUpdate, setPlacementsDataForUpdate] = useState<IPlacementDataForUpdate[]>(
    []
  )
  const [minLimitedCPCByCampaignIdMap, setMinLimitedCPCByCampaignIdMap] = useState<
    Record<string, number>
  >({})

  const { dateRange, isOrder, countryCode } = useTypedSelector((select) => select.filter)
  const campaigns = useTypedSelector((state) => state.sponsoredProductsSKC.campaignData.campaigns)
  const isLoading = useTypedSelector((state) => state.sponsoredProductsSKC.isLoading)
  const { childData } = useTypedSelector((select) => select.placements)

  const { fetchSponsoredProduct, fetchPlacements } = useActions()

  const { calcAllMetrics } = usePlacementMetrics({
    setPlacementsDataForUpdate,
    setSelectedCampaignIds,
    setMinLimitedCPCByCampaignIdMap,
  })

  function refetchCampaigns() {
    fetchSponsoredProduct(
      buildFetchSponsoredProductParams({ dateRange, isOrder, filterIds: selectCustomFilters })
    )
  }

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      refetchCampaigns()
    }
  }, [dateRange.from, dateRange.to, isOrder, selectCustomFilters, countryCode])

  useEffect(() => {
    if (isExpandedAll && campaigns?.length && dateRange.from && dateRange.to) {
      chunk(
        campaigns.map((campaign) => campaign.campaignId),
        250
      ).forEach((campaignIds) => {
        fetchPlacements({
          name: dataRepresentationEnum.PLACEMENT,
          type: weekFilterEnum.DAY,
          campaignNames: campaignIds,
          fromDate: dateRange.from,
          toDate: dateRange.to,
          filterIds: [],
          filterType: isOrder ? ORDER_TYPES.ORDERS : ORDER_TYPES.DATE,
        })
      })
    }
  }, [isExpandedAll, campaigns])

  const processedPlacementsRef = useRef(new Map<number, unknown>())

  useEffect(() => {
    const processedPlacements = processedPlacementsRef.current
    const changedPlacements = childData.placementSubResult.filter(
      (placement) => processedPlacements.get(Number(placement.campaignId)) !== placement
    )

    if (changedPlacements.length === 0) {
      return
    }

    const recalculatedPlacements: IPlacementDataForUpdate[] = changedPlacements.map((placement) => {
      processedPlacements.set(Number(placement.campaignId), placement)
      return {
        campaignId: Number(placement.campaignId),
        placements: calcAllMetrics(placement.placementsData, Number(placement.campaignId)),
      }
    })

    setPlacementsDataForUpdate((prev) => {
      const recalculatedIds = new Set(
        recalculatedPlacements.map((placement) => placement.campaignId)
      )
      return [
        ...prev.filter((placement) => !recalculatedIds.has(placement.campaignId)),
        ...recalculatedPlacements,
      ]
    })
  }, [childData.placementSubResult])

  const kpi = useMemo(() => {
    const rows = campaigns ?? []
    let spend = 0
    let sales = 0
    let active = 0
    for (const c of rows) {
      spend += Number(c.totalSpend) || 0
      sales += Number(c.totalSales) || 0
      if (c.campaignStatus === campaignStates.ENABLED) active += 1
    }
    return { spend, sales, active, total: rows.length }
  }, [campaigns])

  return (
    <Flex vertical gap={16}>
      <KpiSummaryCards
        totalSpend={kpi.spend}
        totalSales={kpi.sales}
        activeCampaigns={kpi.active}
        totalCampaigns={kpi.total}
        loading={isLoading}
      />
      <Settings
        isExpandedAll={isExpandedAll}
        setIsExpandedAll={setIsExpandedAll}
        selectedCampaignIds={selectedCampaignIds}
        setSelectedCampaignIds={setSelectedCampaignIds}
        setSelectCustomFilters={setSelectCustomFilters}
        selectCustomFilters={selectCustomFilters}
        setPlacementsDataForUpdate={setPlacementsDataForUpdate}
        placementDataForUpdate={placementDataForUpdate}
        minLimitedCPCByCampaignIdMap={minLimitedCPCByCampaignIdMap}
      />
      <ProjectGroups
        isExpandedAll={isExpandedAll}
        setSelectedCampaignIds={setSelectedCampaignIds}
        selectedCampaignIds={selectedCampaignIds}
        placementDataForUpdate={placementDataForUpdate}
        setPlacementsDataForUpdate={setPlacementsDataForUpdate}
        minLimitedCPCByCampaignIdMap={minLimitedCPCByCampaignIdMap}
        setMinLimitedCPCByCampaignIdMap={setMinLimitedCPCByCampaignIdMap}
        refetchCampaigns={refetchCampaigns}
      />
    </Flex>
  )
}

export default SponsoredProductsContainer
